import React, { useState, useEffect, useRef } from "react";
import { Card, Typography, Box, Stack, IconButton, Tooltip, Snackbar, Alert, CircularProgress } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDashboardData } from "../../../contexts/DashboardDataContext";
import { useAuth } from "../../AuthContext";
import refreshManager from '../../../utils/refreshManager';

const DataSourceWidget = () => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const { userData: contextUserData, getLastFetched, forceGlobalRefresh, fetchUserData } = useDashboardData();
    const { logout, user } = useAuth();
    const initialized = useRef(false);
    const [lastFetched, setLastFetched] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Oppdatert: Bruk context sin fetchUserData
    const fetchUserDataFromContext = async (force = false) => {
        setLoading(true);
        try {
            const data = await fetchUserData(force);
            setUserData(data);
            setLastUpdated(new Date());
        } catch (error) {
            setUserData(null);
        } finally {
            setLoading(false);
            initialized.current = true;
        }
    };

    useEffect(() => {
        if (contextUserData && contextUserData.data && !(contextUserData.loading)) {
            setUserData(contextUserData.data);
            setLastUpdated(new Date());
            setLoading(false);
            initialized.current = true;
            const ts = getLastFetched ? getLastFetched('userData') : null;
            if (ts) setLastFetched(new Date(ts));
        } else if (!initialized.current && (!userData || (Array.isArray(userData) && userData.length === 0)) && !(contextUserData && contextUserData.loading)) {
            fetchUserDataFromContext();
        }
    }, [contextUserData, userData, getLastFetched]);

    // Add a handler to refresh data on card click
    const handleCardClick = () => {
        if (!loading && !(contextUserData && contextUserData.loading)) {
            fetchUserDataFromContext();
        }
    };

    // Oppdatert handler for refresh-knappen
    const handleRefreshClick = async () => {
        if (loading || (contextUserData && contextUserData.loading)) return;
        setLoading(true);
        try {
            await forceGlobalRefresh();
            await fetchUserDataFromContext(true); // alltid force API fetch etter global refresh
            refreshManager.triggerRefresh('/activity/assigned');
            refreshManager.triggerRefresh('/activity/open-cases');
            setSnackbar({ open: true, message: 'Dashboard data updated!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Could not update now', severity: 'warning' });
        } finally {
            setLoading(false);
        }
    };

    if (!userData) {
        if (loading) {
            return (
                <Card sx={{ width: 300, padding: 2, backgroundColor: "#1e1e1e", color: "#fff" }}>
                    <Typography variant="h6" align="center">Data Source</Typography>
                    <Typography variant="h5" align="center">Loading...</Typography>
                </Card>
            );
        }
        return (
            <Card sx={{ width: 300, padding: 2, backgroundColor: "#1e1e1e", color: "#fff" }}>
                <Typography variant="h6" align="center">Data Source</Typography>
                <Typography variant="h5" align="center">No data available</Typography>
                <button onClick={() => fetchUserDataFromContext()} style={{ margin: '10px auto', display: 'block' }}>
                    Retry
                </button>
            </Card>
        );
    }

    return (
        <Card sx={{ width: 300, padding: 2, backgroundColor: "#1e1e1e", color: "#fff", position: 'relative' }}>
            <Tooltip title="Oppdater alle dashboard-data">
                <span>
                    <IconButton
                        size="small"
                        onClick={handleRefreshClick}
                        disabled={loading || (contextUserData && contextUserData.loading)}
                        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}
                    >
                        {loading || (contextUserData && contextUserData.loading)
                            ? <CircularProgress size={18} color="inherit" />
                            : <RefreshIcon />}
                    </IconButton>
                </span>
            </Tooltip>
            <Stack spacing={2}>
                <h3>Data Source</h3>
                <Box sx={{ 
                    backgroundColor: '#444',
                    padding: 2,
                    borderRadius: 1,
                    border: '1px solid #555'
                }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Group: {userData.persgroup}</Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>District: {userData.persdistr}</Typography>
                    <Typography variant="h6">Database: {userData.sesamdb}</Typography>
                </Box>
                {(lastFetched || lastUpdated) && (
                    <Typography variant="caption" align="center" sx={{ mt: 1, color: "gray" }}>
                        Last updated: {(lastFetched || lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                )}
                {contextUserData && contextUserData.loading && (
                    <Typography variant="caption" align="center" sx={{ color: "gray" }}>
                        Refreshing...
                    </Typography>
                )}
            </Stack>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default DataSourceWidget;
